import { useLayoutEffect, useRef, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Avatar } from '../../../avatar/avatar.component';
import { Button } from '../../../button/button.component';
import { ImagePreview } from '../../../image-preview/image-preview.component';
import { useImagePreviewModalControl } from '../../../image-preview/provider/image-preview-modal-controls';
import { SkeletonLoader } from '../../../loader/skeleton-loader/skeleton-loader';
import { Modal } from '../../../modal/modal';
import { useModalControl } from '../../../modal/provider/use-modal-control';
import { Text } from '../../../text/text.component';
import { useChatBubbleContext } from '../../chat';
import { ChatStyles } from '../../chat.styles';

const imageBubbleFigureStyle1 = css`
  margin-top: 0px;
  margin-right: 0px;
  margin-bottom: 0px;
  margin-left: 0px;
`;

const clickableImgStyle = css`
  cursor: pointer;
`;

const nonClickableImgStyle = css`
  cursor: default;
`;

const loadedImgStyle = css`
  display: block;
`;

const hiddenImgStyle = css`
  display: none;
`;

const previewModalDivStyle3 = css`
  display: flex;
  align-items: center;
`;

const previewModalDivStyle4 = css`
  display: flex;
  flex-direction: column;
  margin-left: ${theme.spacing(1)};
`;

const previewModalTextStyle5 = css`
  display: flex;
`;

const previewModalImgStyle6 = css`
  max-width: 70vw;
  max-height: 90vh;
  padding-top: 0px;
  padding-right: 0px;
  padding-bottom: 0px;
  padding-left: 0px;
`;

const IMAGE_SKELETON_SIZE = {
  width: 'min(70vw, 320px)',
  height: 'min(40vh, 480px)',
} as const;

type ImageBubbleProps = {
  src: string;
  suppressPreview?: boolean;
  onLoad?: () => void;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onClickDownload?: () => void;
  downloadImageTrackingId?: string;
};
export const ImageBubble = ({
  src,
  suppressPreview,
  onLoad,
  onError,
  onClickDownload,
  downloadImageTrackingId,
}: ImageBubbleProps) => {
  const [error, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const suppressOnLoadRef = useRef(false);
  const previewModalProps = useModalControl();
  const { triggerProps, modalProps: imagePreviewModalProps } = useImagePreviewModalControl();
  const { senderName, timestamp } = useChatBubbleContext();

  useLayoutEffect(() => {
    setError(false);
    setIsLoaded(false);

    const img = imgRef.current;
    // Avoid retriggering parent onLoad side effects in case the blob URL has been decoded already
    if (img?.complete && img.naturalWidth > 0) {
      suppressOnLoadRef.current = true;
      setIsLoaded(true);
      return;
    }

    suppressOnLoadRef.current = false;
  }, [src]);

  return (
    <>
      <figure {...(suppressPreview && src ? {} : triggerProps)} className={imageBubbleFigureStyle1}>
        {!error && !isLoaded && (
          <SkeletonLoader
            width={IMAGE_SKELETON_SIZE.width}
            height={IMAGE_SKELETON_SIZE.height}
            animation='shimmer'
            data-testid='chat-image-skeleton-loader'
          />
        )}
        {src && !error && (
          <img
            ref={imgRef}
            className={cx(
              'img-bubble',
              !suppressPreview ? clickableImgStyle : nonClickableImgStyle,
              isLoaded ? loadedImgStyle : hiddenImgStyle
            )}
            src={src}
            onLoad={() => {
              setIsLoaded(true);

              if (suppressOnLoadRef.current) {
                suppressOnLoadRef.current = false;
                return;
              }

              onLoad?.();
            }}
            onError={(e) => {
              setError(true);
              suppressOnLoadRef.current = false;
              onError?.(e);
            }}
          />
        )}
        {error && (
          <>
            <Icon name='warning-badge' color='error' />
            <Text>There was an error loading this image.</Text>
          </>
        )}
      </figure>
      {src && (
        <PreviewModal
          src={src}
          previewModalProps={previewModalProps}
          onClickDownload={onClickDownload}
          downloadImageTrackingId={downloadImageTrackingId}
          imagePreviewModalProps={imagePreviewModalProps}
          senderName={senderName}
          timestamp={timestamp}
        />
      )}
    </>
  );
};

type PreviewModalProps = {
  src: string;
  previewModalProps: ReturnType<typeof useModalControl>;
  onClickDownload?: () => void;
  downloadImageTrackingId?: string;
  imagePreviewModalProps: ReturnType<typeof useImagePreviewModalControl>['modalProps'];
  senderName?: string;
  timestamp?: string | Date;
};
export const PreviewModal = ({
  src,
  previewModalProps,
  onClickDownload,
  downloadImageTrackingId,
  imagePreviewModalProps,
  senderName,
  timestamp,
}: PreviewModalProps) => {
  const modalStyles = ChatStyles.previewModal;

  //Trying the new ImagePreview Modal, will add a wetag to switch
  return (
    <ImagePreview src={src} alt='Image Preview' onClose={imagePreviewModalProps.onClose}>
      <ImagePreview.Modal
        {...imagePreviewModalProps}
        heading={
          <div className={previewModalDivStyle3}>
            <Avatar name={senderName} size='small' />
            <div className={previewModalDivStyle4}>
              <Text color='white' size='small' className={previewModalTextStyle5}>
                {senderName}
              </Text>
              <Text color='white' size='small'>
                {timestamp?.toString()}
              </Text>
            </div>
          </div>
        }
      >
        <ImagePreview.Body>
          <ImagePreview.ImageContent />
        </ImagePreview.Body>
        <ImagePreview.Controls />
      </ImagePreview.Modal>
    </ImagePreview>
  );
  return (
    <Modal {...previewModalProps.modalProps} className={modalStyles}>
      <div className='btn-containers'>
        {onClickDownload && (
          <Button
            onClick={onClickDownload}
            iconName='download'
            variant='secondary'
            className='download-button'
            {...(downloadImageTrackingId && { trackingId: downloadImageTrackingId })}
          />
        )}
        <Button
          size='small'
          onClick={previewModalProps.closeModal}
          className='close-button'
          variant='secondary'
          iconName='x'
        />
      </div>
      <Modal.Body>
        <img src={src} className={previewModalImgStyle6} />
      </Modal.Body>
    </Modal>
  );
};
